import { useSetContainerValues } from '@/components/container/context/context';
import { SkeletonWooDark } from '@/components/skeleton/skeleton.woo-dark';
import { formatPhone } from '@/lib';
import { type UsernameAlias } from '@/portal-api.definition';
import { WooChevronRight } from '@nosinovacao/fe-icons';
import { ActionCard, useI18n } from '@nosinovacao/nosid-mfe-common';
import { Link, Outlet } from '@tanstack/react-router';
import { type HomePageProps } from '../-model/props';

export function HomePageWooDark({ isLoading, usernameAliases }: Readonly<HomePageProps>) {
  const { t } = useI18n();
  useSetContainerValues({
    title: t('Home.Title'),
  });

  return (
    <>
      <main className="grid gap-2 desktop-md:grid-cols-[1fr_minmax(21.25rem,max-content)] desktop-md:gap-x-6 desktop-md:gap-y-4">
        <SkeletonWooDark loading={isLoading} className="h-48 w-full">
          <UsernameAliasCard aliases={usernameAliases} />
        </SkeletonWooDark>
        <div className="flex flex-col gap-2">
          <SkeletonWooDark loading={isLoading} className="h-[92px] w-full">
            <ChangePasswordCard />
          </SkeletonWooDark>
        </div>
      </main>
      <Outlet />
    </>
  );
}

function UsernameAliasCard({ aliases: usernameAliases }: Readonly<{ aliases: UsernameAlias[] }>) {
  const { t } = useI18n();
  const emailAlias = usernameAliases.find((al) => al.Type === 'Email');
  const mobileAlias = usernameAliases.find((al) => al.Type === 'Mobile');
  const otherAlias = usernameAliases.find((al) => al.Type === 'Other');
  const aliases: UsernameAlias[] = [
    {
      Type: 'Mobile',
      Username: mobileAlias?.Username ? formatPhone(mobileAlias.Username) : '',
    },
    {
      Type: 'Email',
      Username: emailAlias?.Username ?? '',
    },
  ];

  if (otherAlias) {
    aliases.push({
      Type: 'Other',
      Username: otherAlias.Username,
    });
  }
  return (
    <div className="grid gap-5 rounded border border-nos-neutral-700 bg-nos-neutral-900 p-4">
      <span className="font-medium text-nos-neutral-0 font-title-h4" data-tid="title-login-aliases">
        {t('Home.LoginUsernameAliases')}
      </span>
      {aliases.map((al) => (
        <div className="grid gap-1" key={al.Type}>
          <span className="text-nos-neutral-200 font-text-p2">
            {t(`Home.UsernameAlias.${al.Type}`)}
          </span>
          <span
            className="text-nos-neutral-0 font-text-p1"
            data-tid={`text-alias-${al.Type.toLowerCase()}`}
          >
            {al.Username || t('Home.UsernameAlias.Default')}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChangePasswordCard() {
  const { t } = useI18n();
  return (
    <ActionCard
      skin="woo-dark"
      variant="quick-link"
      preTitle={t('Home.Password')}
      subTitle={<></>}
      title={t('Home.ActionChange')}
      icon={<WooChevronRight width={20} height={20} />}
      className="w-full border-nos-neutral-700 bg-nos-neutral-900"
      data-tid="card-change-password"
      asChild
    >
      <Link to="/home/change-password" />
    </ActionCard>
  );
}
